/* eslint-disable i18next/no-literal-string */
import Header from "~/components/header/Header";
import Footer from "~/components/home/Footer";
import {
  LandingPageHeaderH2,
  LandingPageSubheader,
} from "~/components/landing/LandingText";
import Pricing from "~/components/landing/Pricing";
import { AddToCartButton } from "~/components/landing/Pricing";
import AllFeatures, { ShortSentences } from "~/components/landing/AllFeatures";
import DeviceFeatures from "~/components/landing/DeviceFeatures";
import PrivacyPromised from "~/components/landing/PrivacyPromised";
import EasyInstallation, {
  EasyInstallationSubText,
} from "~/components/landing/EasyInstallation";
import ObdCompatibility from "~/components/landing/ObdCompatibility";
import Reviews from "~/components/landing/Reviews";
import ReviewsAndRatings, {
  type LandingReview,
} from "~/components/landing/ReviewsAndRatings";
import BlogLinks, {
  type LandingBlogLinksData,
} from "~/components/landing/BlogLinks";
import CartAndPricing from "~/components/landing/CartAndPricing";
import MiscGridRentals from "~/components/landing/MiscGridRentals";
import MonitoringHighlightsGrid, {
  type MonitoringHighlightsCopy,
} from "~/components/landing/MonitoringHighlightsGrid";
import TuroHero from "~/components/turo/TuroHero";
import InsuranceIntegrations from "~/components/turo/InsuranceIntegrations";
import TuroVideoSection from "~/components/turo/TuroVideoSection";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const satisfiedCustomers = imageUrl("/images/landing/three-satisfied-fleet-customers.jpg");
const keyHandOff = imageUrl("/images/turo/key-hand-off.jpg");
const geoZoneSetup = imageUrl("/images/turo/geo-zone-setup.png");
const carSharing = imageUrl("/images/turo/car-sharing.jpg");
const turoHost1 = imageUrl("/images/turo/reviewer-image-1.png");
const turoHost2 = imageUrl("/images/landing/reviewer-image-2.png");
const turoHost3 = imageUrl("/images/turo/reviewer-image-3.png");

// Ported from bouncieweb's `pages/turo.js` -> `components/LandingTuro.js`
// (rendered there with the title/description below and `footerLite` defaulting
// to false, so the full Footer is used).
// Faithful reproduction: same section order, copy, and layout as the original.

const blogLinksData: LandingBlogLinksData = {
  heading: "Insights, Tips, and Management Strategies for Turo Hosts",
  paragraph: `Get the most out of every mile with expert tips, performance insights,
                and smart strategies to keep your vehicles and your business running smoothly.
                Explore the latest guides and updates from the Bouncie team to manage your Turo
                fleet with confidence.`,
  blogs: [
    {
      href: "https://www.bouncie.com/blog/gps-tracking-for-turo-hosts-stay-compliant-and-in-control",
      image: keyHandOff,
      imageAlt:
        "Two people exchanging a car key in front of a shiny blue sports car in a parking lot under a clear blue sky, symbolizing a vehicle handoff or car-sharing transaction.",
      label: "Industry Insights",
      title: "GPS Tracking for Turo Hosts: Stay Compliant and In Control",
    },
    {
      href: "https://www.bouncie.com/blog/how-to-set-up-geo-zones-with-bouncie",
      image: geoZoneSetup,
      imageAlt:
        "The perspective is a phone laying on a angle and a circular geofenced area is coming out of the phone onto a map behind the phone showing the versatility of Geo-Zones within the Bouncie app.",
      label: "How To",
      title: (
        <>
          How to Set Up <span className="whitespace-nowrap">Geo-Zones</span>{" "}
          with Bouncie
        </>
      ),
    },
    {
      href: "https://www.bouncie.com/blog/optimize-your-car-sharing-business-with-gps-tracking",
      image: carSharing,
      imageAlt:
        "A clean, modern car is parked in a residential curbside. Two men stand next to the vehicle. The renter is being handed keys to the vehicle. ",
      label: "Industry Insights",
      title: "Optimize Your Car-Sharing Business with GPS Tracking",
    },
  ],
};

// The seven strings that make the shared monitoring grid Turo's.
const highlightsCopy: MonitoringHighlightsCopy = {
  locationSubtitle: "Real-time visibility.",
  locationBody: (
    <>
      Whether it&rsquo;s one vehicle or a growing fleet, Bouncie captures every
      second of each trip, past and present, all on one live map. From handoff to
      drop-off, you&rsquo;ll always know where your vehicle is. Host with
      confidence!
    </>
  ),
  habitsOverlayTitle: "Their driving style.",
  habitsBody: (
    <>
      Get instant insights on how your guest drives: speed, acceleration, hard
      braking, and more. Safer trips start with visibility. Their habits. Your
      advantage!
    </>
  ),
  accidentBody: (
    <>
      Bouncie&rsquo;s advanced sensors and software detect accidents the moment
      they happen and instantly send you an SMS alert. No guesswork, no delay.
      React quickly. Protect what matters.
    </>
  ),
  weatherBody: (
    <>
      We&rsquo;re watching out for you. When a severe weather advisory is issued
      near your vehicle, Bouncie automatically sends a message with all the
      details. We got this!
    </>
  ),
  geoZonesBody: (
    <>
      Get notified the moment your vehicle enters or exits a zone you set.
      Whether it&rsquo;s a pickup spot, drop-off location, or off-limits area,
      Bouncie keeps you one step ahead. No checking required!
    </>
  ),
};

const reviewsData: LandingReview[] = [
  {
    image: turoHost1,
    name: "Jonathan S",
    rating: 5,
    title: "Amazing product.",
    body: (
      <>
        ...I purchased this for my Turo rental vehicle. This thing is amazing.
        It&rsquo;s extremely accurate, shows you in live time where your vehicle
        is, how fast it&rsquo;s going, how much gas it has... there&rsquo;s just
        so much information on such an intuitive and easy to use app.
      </>
    ),
  },
  {
    image: turoHost2,
    name: "T. Rivas",
    rating: 5,
    title: "Ideal for Fleet Management!",
    body: (
      <>
        The Bouncie GPS Car Tracker is perfect for my rental car business! Its
        4G LTE connectivity offers reliable vehicle location tracking, route
        history, and speed monitoring. The accident notification and GeoFence
        features provide extra security and peace of mind... Highly recommend!
      </>
    ),
  },
  {
    image: turoHost3,
    name: "David A.K.",
    rating: 5,
    title: "Bouncie is worth every penny.",
    body: (
      <>
        I have Bouncie installed on two vehicles that I rent on Turo. I am
        delighted with the product. It is very accurate and provides various
        settings that can be adjusted to your needs.
      </>
    ),
  },
];

export default function Turo() {
  return (
    <>
      {/* React 19 hoists these into the document <head> (no wrapper element —
          a nested <head> collides with root.tsx's document head). The original
          built the canonical from `window.location.origin`; pinned to a literal
          here so the server and client markup agree. */}
      <title>GPS Tracker for Turo Hosts | Bouncie</title>
      <meta
        name="description"
        content="Peace of mind for every handoff. Bouncie helps Turo hosts stay in control with live tracking, alerts, & insights for every trip. Easy setup & low monthly cost."
      />
      <link rel="canonical" href="https://www.bouncie.com/turo" />

      <Header />

      {/* LandingPageGlobalStyles: color fadedBlack2 + balanced text wrap */}
      <div className="[text-wrap:balance] text-[#0B1019]">
        <TuroHero />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <LandingPageHeaderH2>
            Smarter Hosting Starts&nbsp;Here
          </LandingPageHeaderH2>
          <LandingPageSubheader>
            From key handoff to trip return, Bouncie keeps you informed every
            mile.
          </LandingPageSubheader>
          <MiscGridRentals />
        </div>

        <ReviewsAndRatings
          heading="Trusted by Turo Hosts"
          reviewsData={reviewsData}
        />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <Pricing className="mb-12" showQuoteButton />
          <InsuranceIntegrations />
          <TuroVideoSection />
          <LandingPageHeaderH2>Know more. Worry&nbsp;less.</LandingPageHeaderH2>
          <LandingPageSubheader>
            The ultimate connected driving app used in over 30,000 Turo
            vehicles.
          </LandingPageSubheader>
          <MonitoringHighlightsGrid {...highlightsCopy} />
        </div>

        <AllFeatures
          bottomContent={
            <>
              <div className="mt-4 mb-8">
                <ShortSentences>No contracts.</ShortSentences>
                <ShortSentences>No hidden fees.</ShortSentences>
                <ShortSentences>No activation fees.</ShortSentences>
              </div>
              {/* CustomAddToCartButton: 18px text-bold, 1rem/1.5rem padding,
                  width unset at every breakpoint. */}
              <AddToCartButton data-sid="turo-add-to-cart" className="w-auto px-6 py-4 font-bold md:w-auto" />
            </>
          }
        />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <DeviceFeatures />
          <PrivacyPromised className="mt-0 mb-16" />
          <EasyInstallation
            comboBottom
            preHeading={<>Yes, it&rsquo;ll work in your vehicle!</>}
            heading={
              <>
                <EasyInstallationSubText>No tools.</EasyInstallationSubText>
                <EasyInstallationSubText>No wires.</EasyInstallationSubText>
                <EasyInstallationSubText>
                  It just plugs in.
                </EasyInstallationSubText>
              </>
            }
          />
          <div id="obd-compatibility-scroll-target" />
          <ObdCompatibility comboTop />
          <Reviews
            carAndDriverImageAlt="Two women on a journey in a car"
            forbesImage={satisfiedCustomers}
            forbesImageAlt="Three smiling customers"
          />
        </div>

        <BlogLinks blogLinksData={blogLinksData} />
        <CartAndPricing />

        <Footer />
      </div>
    </>
  );
}
